import type {
  Alert,
  Phone,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ExclamationOutlineIcon,
  Label,
} from "@tandemdiabetes/sugarmate-react-components"
import classNames from "classnames"
import { useMemo } from "react"

import { AddPhoneNumberButton } from "./AddPhoneNumberButton"
import styles from "./PhoneNumber.module.scss"
import {
  PhoneNumberToggle,
  phoneNumberToggleId,
} from "./PhoneNumberToggle"
import { useOnAddPhone } from "./useOnAddPhone"
import { PhoneNumberModal } from "src/components"
import { usePolyglot } from "src/contexts"
import { formatPhoneNumber } from "src/utils"

const polyglotPrefix = "pages.alerts.phone_number."

interface Props {
  primaryPhone: Phone | undefined;
  maxPhoneNumbers: number;
  sugarmateAlert: Alert;
  updateAlert: (settings: Partial<Alert>) => void;
}

export function PhoneNumber(props: Props) {
  const {
    primaryPhone,
    maxPhoneNumbers,
    sugarmateAlert,
    updateAlert,
  } = props

  const polyglot = usePolyglot()

  const phoneNumber = useMemo(
    () => primaryPhone ? formatPhoneNumber(primaryPhone.phone_number) : "",
    [primaryPhone],
  )

  /*
   * hide the primary phone toggle if the user's primary phone is the only
   * enabled phone number for this alert
   */
  const hidePrimaryPhoneToggle = useMemo(
    () => !!primaryPhone &&
      sugarmateAlert.phone_ids.length === 1 &&
      sugarmateAlert.phone_ids[0] === primaryPhone.id,
    [
      sugarmateAlert.phone_ids,
      primaryPhone,
    ],
  )

  const cannotAddPhoneNumber = useMemo(
    () => !!primaryPhone &&
      !sugarmateAlert.phone_ids.includes(primaryPhone.id) &&
      sugarmateAlert.phone_ids.length >= maxPhoneNumbers,
    [
      primaryPhone,
      maxPhoneNumbers,
      sugarmateAlert.phone_ids,
    ],
  )

  const onSuccess = useOnAddPhone(
    primaryPhone,
    maxPhoneNumbers,
    sugarmateAlert,
  )

  return (
    <PhoneNumberModal onSuccess={onSuccess}>
      <div id="user-phone-number">
        <Label
          className={styles.inputLabel}
          htmlFor={hidePrimaryPhoneToggle ? undefined : phoneNumberToggleId}
        >
          {polyglot.t(`${polyglotPrefix}label`)}
        </Label>
        <div className={styles.phoneNumberRow}>
          {primaryPhone && phoneNumber ? (
            <>
              <div className={styles.phoneNumber}>
                {phoneNumber}
              </div>
              {!hidePrimaryPhoneToggle && (
                <PhoneNumberToggle
                  sugarmateAlert={sugarmateAlert}
                  phone={primaryPhone}
                  updateAlert={updateAlert}
                  disabled={cannotAddPhoneNumber}
                />
              )}
            </>
          ) : (
            <>
              <div className={classNames(styles.noPhone)}>
                <ExclamationOutlineIcon
                  className={styles.noPhoneIcon}
                  height={20}
                  width={20}
                />
                <div className={styles.noPhoneText}>
                  {polyglot.t(`${polyglotPrefix}no_phone_message`)}
                </div>
              </div>
              <AddPhoneNumberButton />
            </>
          )}
        </div>
        {cannotAddPhoneNumber && (
          <div className={styles.removePhoneMessage}>
            {polyglot.t(`${polyglotPrefix}remove_phone_message`)}
          </div>
        )}
      </div>
    </PhoneNumberModal>
  )
}
